/**
 * Multi-select display values are comma-joined labels — the same shape the
 * document read mapper uses for `valueIds` (`Document/mappers.ts`). Option
 * ids stay in a parallel array because the backend persists `valueIds`.
 */

/** How joined labels are stored, and the only sequence they are split back on. */
export const MULTI_VALUE_SEPARATOR = ", ";

/** Split a stored multi-select display value back into the picked labels. */
export const splitMultiValue = (value: string | undefined): string[] =>
  value ? value.split(MULTI_VALUE_SEPARATOR).filter(Boolean) : [];

/**
 * Split a joined display value using known option labels so a label that
 * contains `", "` (e.g. `Boston, MA`) is not cut into two picks.
 *
 * Longest-label match at each position; unmatched chunks are kept as naive
 * tokens so an unknown fallback id is not dropped. With no options, falls
 * back to {@link splitMultiValue}.
 */
export const splitMultiValueAgainstOptions = (
  value: string | undefined,
  options: readonly string[],
): string[] => {
  if (!value) return [];
  if (options.length === 0) return splitMultiValue(value);
  const byLength = [...options].filter(Boolean).sort((a, b) => b.length - a.length);
  const picked: string[] = [];
  let remaining = value;
  while (remaining.length > 0) {
    const match = byLength.find(
      (opt) => remaining === opt || remaining.startsWith(`${opt}${MULTI_VALUE_SEPARATOR}`),
    );
    if (!match) {
      const sep = remaining.indexOf(MULTI_VALUE_SEPARATOR);
      if (sep < 0) {
        picked.push(remaining);
        break;
      }
      picked.push(remaining.slice(0, sep));
      remaining = remaining.slice(sep + MULTI_VALUE_SEPARATOR.length);
      continue;
    }
    picked.push(match);
    remaining = remaining.slice(match.length);
    if (remaining.startsWith(MULTI_VALUE_SEPARATOR)) {
      remaining = remaining.slice(MULTI_VALUE_SEPARATOR.length);
    }
  }
  return picked;
};

/** Join picked labels into the single display value the form stores. */
export const joinMultiValue = (values: string[]): string => values.join(MULTI_VALUE_SEPARATOR);

/**
 * The one id behind a pick. A multi-select entry has no single id, so callers
 * that genuinely need one (client scoping, the Number reference coercion) read
 * the first rather than a comma-joined value no backend would accept.
 */
export const singleRefId = (ref: string | string[] | undefined): string | undefined =>
  Array.isArray(ref) ? ref[0] : ref;

/** Every resolved id behind a pick, single or multi; unresolved entries drop out. */
export const refIdList = (ref: string | string[] | undefined): string[] =>
  (Array.isArray(ref) ? ref : ref ? [ref] : []).filter(Boolean);

/** Normalize the design-system `Select` change payload to the picked labels. */
export const pickedLabels = (value: string | number | string[] | number[]): string[] =>
  (Array.isArray(value) ? value : [value]).map(String).filter(Boolean);

/** Option ids for the picked labels, in pick order; unmapped labels drop out.
 *
 * Pass `{ keepUnmappedIds: true }` on Document details so a fallback option id
 * shown in the cell is not dropped from `valueIds`. Upload stays on drop —
 * a stale label there must not be sent as an id. */
export const optionIdsFor = (
  picked: string[],
  optionIdsByValue: Record<string, string> | undefined,
  options?: { keepUnmappedIds?: boolean },
): string[] | undefined => {
  if (!optionIdsByValue) return undefined;
  const ids = picked
    .map(
      (p) => optionIdsByValue[p] ?? (options?.keepUnmappedIds && !/\s/.test(p) ? p : undefined),
    )
    .filter((id): id is string => !!id);
  return ids.length ? ids : undefined;
};
